import { Expose, Type } from 'class-transformer';
import {
    IsArray,
    IsBoolean,
    IsEnum,
    IsInt,
    IsObject,
    IsOptional,
    IsString,
    Max,
    MaxLength,
    Min,
    ValidateNested,
} from 'class-validator';

import { IsNotBlank } from '../shared/is-not-blank';

/**
 * Fact candidates are the editorial bank a place story is built from.
 * Claims are always written in Russian, whatever the country; source quotes
 * stay verbatim in the source language.
 */

export enum AttractionFactKindEnum {
    ORIGIN = 'origin',
    ANECDOTE = 'anecdote',
    PERSON = 'person',
    EVENT = 'event',
    ARCHITECTURE = 'architecture',
    VISIBLE_DETAIL = 'visible_detail',
    LEGEND = 'legend',
    RECORD = 'record',
    POP_CULTURE = 'pop_culture',
    EVERYDAY = 'everyday',
    SCALE = 'scale',
    CONTROVERSY = 'controversy',
    TRAGEDY = 'tragedy',
    TODAY = 'today',
    OTHER = 'other',
}

export enum AttractionFactTruthEnum {
    UNCHECKED = 'unchecked',
    CONFIRMED = 'confirmed',
    LEGEND = 'legend',
    DISPUTED = 'disputed',
    UNVERIFIED = 'unverified',
    FALSE = 'false',
}

export enum AttractionFactVerdictEnum {
    PASSED = 'passed',
    NEEDS_CHECK = 'needs_check',
    FAILED = 'failed',
    DUPLICATE = 'duplicate',
}

export enum AttractionFactDecisionEnum {
    PENDING = 'pending',
    TAKE = 'take',
    MUST = 'must',
    REJECT = 'reject',
}

export enum AttractionFactRoleEnum {
    SPINE = 'spine',
    BEAD = 'bead',
    HOOK = 'hook',
    PRIMER = 'primer',
    CARD = 'card',
    FUN_FACT = 'fun_fact',
    LOOK_FOR = 'look_for',
    BACKGROUND = 'background',
}

export enum AttractionFactSensitivityEnum {
    NONE = 'none',
    TRAGEDY = 'tragedy',
    MEMORIAL = 'memorial',
    RELIGION = 'religion',
    POLITICS = 'politics',
}

export enum AttractionFactSourceTypeEnum {
    OFFICIAL = 'official',
    MUSEUM = 'museum',
    ACADEMIC = 'academic',
    ENCYCLOPEDIA = 'encyclopedia',
    ARCHIVE = 'archive',
    PRESS = 'press',
    MEMOIR = 'memoir',
    GUIDEBOOK = 'guidebook',
    WIKI = 'wiki',
    BLOG = 'blog',
    OTHER = 'other',
}

export enum AttractionFactOriginEnum {
    RESEARCH = 'research',
    AGENT = 'agent',
    EDITOR = 'editor',
}

export enum AttractionResearchRunStatusEnum {
    QUEUED = 'queued',
    RUNNING = 'running',
    DONE = 'done',
    FAILED = 'failed',
}

export enum AttractionResearchRunKindEnum {
    /** Collect new candidates across angles and leads. */
    RESEARCH = 'research',
    /** Targeted search that enriches picked facts. */
    DEEPEN = 'deepen',
    /** Merge paraphrases already sitting in the bank. */
    DEDUPE = 'dedupe',
    /** Cheap draft scoring of unreviewed candidates. */
    SCORE = 'score',
    /** One capped search: story potential, hooks and a few facts. */
    SCOUT = 'scout',
}

/** standard — 10 strongest angles and up to 5 leads; deep — every angle and up to 14 leads. */
export enum AttractionResearchDepthEnum {
    /** 6 angles + 3 leads, capped search, Flex tier and a hard dollar cap per run. */
    ECONOMY = 'economy',
    STANDARD = 'standard',
    DEEP = 'deep',
}

export enum AttractionStoryBriefStatusEnum {
    DRAFT = 'draft',
    APPROVED = 'approved',
}

export type AiUsageBucket = {
    calls: number;
    inputTokens: number;
    cachedTokens: number;
    outputTokens: number;
    reasoningTokens: number;
    webSearches: number;
    /** Estimate at list prices; absent in runs saved before cost tracking. */
    costUsd?: number;
};

/** Token and web-search spend of one job, split by stage and by model. */
export type AiUsageSummary = {
    total: AiUsageBucket;
    stages: Record<string, AiUsageBucket>;
    models: Record<string, AiUsageBucket>;
};

export type AttractionFactSource = {
    url?: string | null;
    title?: string | null;
    publisher?: string | null;
    sourceType?: AttractionFactSourceTypeEnum | null;
    /** Verbatim excerpt in the source language. */
    quote?: string | null;
    /** Null — not checked, false — the quote was not found at the URL. */
    grounded?: boolean | null;
};

/** 1–5 per axis; interest is judged separately from truth. */
export type AttractionFactScores = {
    surprise?: number;
    retellability?: number;
    specificity?: number;
    visual?: number;
    humor?: number;
    placeRelevance?: number;
};

export type AttractionFactCandidateRO = {
    id: number;
    createdAt: string | Date;
    updatedAt: string | Date;
    attractionId: number;
    researchRunId: number | null;
    personId: number | null;
    origin: AttractionFactOriginEnum;
    claim: string;
    detail: string | null;
    year: string | null;
    kind: AttractionFactKindEnum;
    truthStatus: AttractionFactTruthEnum;
    sources: AttractionFactSource[];
    sourceGrounded: boolean | null;
    observable: boolean;
    whereToSee: string | null;
    sensitivity: AttractionFactSensitivityEnum;
    volatile: boolean;
    themeTags: string[];
    aiScores: AttractionFactScores | null;
    aiRating: number | null;
    aiVerdict: AttractionFactVerdictEnum | null;
    aiSuggestedRole: AttractionFactRoleEnum | null;
    aiNotes: string | null;
    aiReviewedAt: string | Date | null;
    /** `agent` or `draft:<model>` for the cheap automatic pass. */
    aiReviewer: string | null;
    /** Set when the fact was merged into another one as a paraphrase. */
    duplicateOfId: number | null;
    deepenedAt: string | Date | null;
    editorDecision: AttractionFactDecisionEnum;
    editorRole: AttractionFactRoleEnum | null;
    editorNote: string | null;
    reviewedByEmail: string | null;
    reviewedAt: string | Date | null;
    usedIn: string[];
};

export type AttractionResearchRunLogEntry = { at: string; message: string };

export type AttractionResearchRunRO = {
    id: number;
    createdAt: string | Date;
    attractionId: number;
    kind: AttractionResearchRunKindEnum;
    depth: AttractionResearchDepthEnum | null;
    status: AttractionResearchRunStatusEnum;
    angles: string[];
    factIds: number[];
    model: string | null;
    promptVersion: string | null;
    found: number;
    created: number;
    duplicates: number;
    grounded: number;
    scored: number;
    usage: AiUsageSummary | null;
    log: AttractionResearchRunLogEntry[];
    error: string | null;
    finishedAt: string | Date | null;
};

export type AttractionStoryBriefCard = {
    titleIdea?: string | null;
    factIds: number[];
};

/** Approved before any prose is written; all text is Russian. */
export type AttractionStoryBrief = {
    /** One line that sells the stop, like a card subtitle. */
    promise: string;
    /** The thread every bead hangs on. */
    spine: string;
    hookIdea?: string | null;
    /** Ordered beads for guideTalk. */
    beadIds: number[];
    cards: AttractionStoryBriefCard[];
    funFactId?: number | null;
    lookForIds: number[];
    notes?: string | null;
    status: AttractionStoryBriefStatusEnum;
    author: 'ai' | 'editor';
    updatedAt?: string | null;
    approvedByEmail?: string | null;
    approvedAt?: string | null;
};

export class AttractionFactSourceDto {
    @IsOptional()
    @IsString()
    @Expose()
    url?: string | null;

    @IsOptional()
    @IsString()
    @Expose()
    title?: string | null;

    @IsOptional()
    @IsString()
    @Expose()
    publisher?: string | null;

    @IsOptional()
    @IsEnum(AttractionFactSourceTypeEnum)
    @Expose()
    sourceType?: AttractionFactSourceTypeEnum | null;

    @IsOptional()
    @IsString()
    @Expose()
    quote?: string | null;
}

export class CreateAttractionFactDto {
    @IsNotBlank()
    @MaxLength(2000)
    @Expose()
    claim: string;

    @IsOptional()
    @IsString()
    @Expose()
    detail?: string | null;

    @IsOptional()
    @IsString()
    @Expose()
    year?: string | null;

    @IsOptional()
    @IsEnum(AttractionFactKindEnum)
    @Expose()
    kind?: AttractionFactKindEnum;

    @IsOptional()
    @IsEnum(AttractionFactTruthEnum)
    @Expose()
    truthStatus?: AttractionFactTruthEnum;

    @IsOptional()
    @IsArray()
    @ValidateNested({ each: true })
    @Type(() => AttractionFactSourceDto)
    @Expose()
    sources?: AttractionFactSourceDto[];

    @IsOptional()
    @IsBoolean()
    @Expose()
    observable?: boolean;

    @IsOptional()
    @IsString()
    @Expose()
    whereToSee?: string | null;

    @IsOptional()
    @IsEnum(AttractionFactSensitivityEnum)
    @Expose()
    sensitivity?: AttractionFactSensitivityEnum;

    @IsOptional()
    @IsBoolean()
    @Expose()
    volatile?: boolean;

    @IsOptional()
    @IsArray()
    @IsString({ each: true })
    @Expose()
    themeTags?: string[];

    @IsOptional()
    @IsInt()
    @Expose()
    personId?: number | null;
}

/** Editor update: content fixes plus the human decision. */
export class UpdateAttractionFactDto {
    @IsOptional()
    @IsNotBlank()
    @MaxLength(2000)
    @Expose()
    claim?: string;

    @IsOptional()
    @IsString()
    @Expose()
    detail?: string | null;

    @IsOptional()
    @IsString()
    @Expose()
    year?: string | null;

    @IsOptional()
    @IsEnum(AttractionFactKindEnum)
    @Expose()
    kind?: AttractionFactKindEnum;

    @IsOptional()
    @IsEnum(AttractionFactTruthEnum)
    @Expose()
    truthStatus?: AttractionFactTruthEnum;

    @IsOptional()
    @IsBoolean()
    @Expose()
    observable?: boolean;

    @IsOptional()
    @IsString()
    @Expose()
    whereToSee?: string | null;

    @IsOptional()
    @IsEnum(AttractionFactSensitivityEnum)
    @Expose()
    sensitivity?: AttractionFactSensitivityEnum;

    @IsOptional()
    @IsBoolean()
    @Expose()
    volatile?: boolean;

    @IsOptional()
    @IsArray()
    @IsString({ each: true })
    @Expose()
    themeTags?: string[];

    @IsOptional()
    @IsEnum(AttractionFactDecisionEnum)
    @Expose()
    editorDecision?: AttractionFactDecisionEnum;

    @IsOptional()
    @IsEnum(AttractionFactRoleEnum)
    @Expose()
    editorRole?: AttractionFactRoleEnum | null;

    @IsOptional()
    @IsString()
    @Expose()
    editorNote?: string | null;
}

export class StartAttractionResearchDto {
    /** Explicit subset of angles; overrides the depth profile. */
    @IsOptional()
    @IsArray()
    @IsString({ each: true })
    @Expose()
    angles?: string[];

    /** Defaults to deep for icons and standard for everything else. */
    @IsOptional()
    @IsEnum(AttractionResearchDepthEnum)
    @Expose()
    depth?: AttractionResearchDepthEnum;
}

export class DeepenAttractionFactsDto {
    /** Facts to enrich; defaults to picked (take/must) facts not deepened yet. */
    @IsOptional()
    @IsArray()
    @IsInt({ each: true })
    @Expose()
    factIds?: number[];
}

export class AttractionStoryBriefCardDto {
    @IsOptional()
    @IsString()
    @Expose()
    titleIdea?: string | null;

    @IsArray()
    @IsInt({ each: true })
    @Expose()
    factIds: number[];
}

export class SaveAttractionStoryBriefDto {
    @IsString()
    @Expose()
    promise: string;

    @IsString()
    @Expose()
    spine: string;

    @IsOptional()
    @IsString()
    @Expose()
    hookIdea?: string | null;

    @IsArray()
    @IsInt({ each: true })
    @Expose()
    beadIds: number[];

    @IsArray()
    @ValidateNested({ each: true })
    @Type(() => AttractionStoryBriefCardDto)
    @Expose()
    cards: AttractionStoryBriefCardDto[];

    @IsOptional()
    @IsInt()
    @Expose()
    funFactId?: number | null;

    @IsArray()
    @IsInt({ each: true })
    @Expose()
    lookForIds: number[];

    @IsOptional()
    @IsString()
    @Expose()
    notes?: string | null;

    @IsOptional()
    @IsEnum(AttractionStoryBriefStatusEnum)
    @Expose()
    status?: AttractionStoryBriefStatusEnum;
}

/** Agent scoring payload; touches only ai* fields and the truth status. */
export class ScoreAttractionFactDto {
    @IsInt()
    @Expose()
    factId: number;

    @IsOptional()
    @IsEnum(AttractionFactTruthEnum)
    @Expose()
    truthStatus?: AttractionFactTruthEnum;

    @IsOptional()
    @IsEnum(AttractionFactVerdictEnum)
    @Expose()
    aiVerdict?: AttractionFactVerdictEnum;

    @IsOptional()
    @IsObject()
    @Expose()
    aiScores?: AttractionFactScores;

    @IsOptional()
    @IsInt()
    @Min(0)
    @Max(100)
    @Expose()
    aiRating?: number;

    @IsOptional()
    @IsEnum(AttractionFactRoleEnum)
    @Expose()
    aiSuggestedRole?: AttractionFactRoleEnum | null;

    @IsOptional()
    @IsString()
    @Expose()
    aiNotes?: string | null;
}
